import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ClientReservationService } from '../../../_services/api/reservation-client.service';
import { 
  ReservationDetail, 
  ReservationUpdateDTO,
  ReservationEventType, 
  ReservationEventTypeLabels,
  Address
} from '../../../_models/reservation.model';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';

@Component({
  selector: 'app-member-reservation-update',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './member-reservation-update.html',
  styleUrl: './member-reservation-update.scss',
})
export class MemberReservationUpdate implements OnInit, OnDestroy {
  reservationForm!: FormGroup;
  reservationId!: number;
  reservation: ReservationDetail | null = null;
  isLoading = false;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  
  eventTypes = Object.values(ReservationEventType);
  eventTypeLabels = ReservationEventTypeLabels;
  
  minStartDate = new Date();
  minEndDate = new Date();
  
  frenchTranslations = {
    required: 'Ce champ est requis',
    eventNameRequired: 'Le nom de l\'événement est requis',
    eventNameMaxLength: 'Le nom de l\'événement doit comporter moins de 255 caractères',
    eventTypeRequired: 'Le type d\'événement est requis',
    agentsRequired: 'Le nombre d\'agents est requis',
    agentsMin: 'Au moins 1 agent est requis',
    agentsMax: 'Maximum 50 agents autorisés',
    startDateRequired: 'La date de début est requise',
    startDateFuture: 'La date de début doit être future',
    endDateRequired: 'La date de fin est requise',
    endDateFuture: 'La date de fin doit être future',
    endDateAfterStart: 'La date de fin doit être après la date de début',
    streetNumberMaxLength: 'Le numéro de rue doit comporter moins de 20 caractères',
    streetRequired: 'La rue est requise pour ce type d\'événement',
    streetMaxLength: 'La rue doit comporter moins de 255 caractères',
    postalCodeRequired: 'Le code postal est requis',
    postalCodeMaxLength: 'Le code postal doit comporter moins de 20 caractères',
    postalCodePattern: 'Veuillez saisir un code postal valide (lettres, chiffres, espaces et tirets uniquement)',
    cityRequired: 'La ville est requise',
    cityMaxLength: 'La ville doit comporter moins de 100 caractères',
    descriptionMaxLength: 'La description doit comporter moins de 1000 caractères',
    unsavedChanges: 'Vous avez des modifications non enregistrées',
    updatingReservation: 'Mise à jour de réservation en cours...',
    confirmUpdate: 'Mettre à jour',
    reservationUpdateSuccess: 'Réservation mise à jour avec succès !',
    reservationUpdateFailed: 'Échec de la mise à jour de la réservation. Veuillez réessayer.',
    loadingForm: 'Chargement du formulaire...',
    eventTypes: {
      SECURITY_GUARDING: 'Garde de Sécurité',
      EVENT_SECURITY: 'Sécurité Événementielle',
      PERSONAL_PROTECTION: 'Protection Personnelle',
      SURVEILLANCE: 'Surveillance',
      SECURITY_AUDIT: 'Audit de Sécurité',
      TRANSPORT_SECURITY: 'Sécurité de Transport',
      OTHER: 'Autre'
    }
  };
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private reservationService: ClientReservationService,
    private notificationService: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadReservationId();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadReservationId(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      
      if (id) {
        this.reservationId = +id;
        this.loadReservationDetails();
      } else {
        this.errorMessage = 'ID de réservation non trouvé';
        setTimeout(() => this.goToDetails(), 2000);
      }
    });
  }

  private loadReservationDetails(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.reservationService.getReservationDetails(this.reservationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<ReservationDetail>) => {
          if (response.success && response.data) {
            this.reservation = response.data;
            this.populateForm();
          } else {
            this.errorMessage = response.message || 'Erreur lors du chargement des données';
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading reservation details:', error);
          this.errorMessage = 'Erreur de chargement des détails de la réservation';
          this.isLoading = false;
          
          if (error.status === 404) {
            this.errorMessage = 'Réservation non trouvée';
            setTimeout(() => this.goToDetails(), 2000);
          }
        }
      });
  }

  private initializeForm(): void {
    this.reservationForm = this.fb.group({
      eventName: ['', [
        Validators.required,
        Validators.maxLength(255)
      ]],
      reservationEventType: ['', Validators.required],
      requiredAgents: [1, [
        Validators.required,
        Validators.min(1),
        Validators.max(50)
      ]],
      startDateTime: ['', [
        Validators.required,
        this.futureDateValidator()
      ]],
      endDateTime: ['', [
        Validators.required,
        this.futureDateValidator()
      ]],
      description: ['', Validators.maxLength(1000)],
      address: this.fb.group({
        street: ['', Validators.maxLength(255)],
        streetNumber: ['', Validators.maxLength(20)],
        postalCode: ['', [
          Validators.maxLength(20),
          Validators.pattern(/^[0-9A-Z\s\-]*$/)
        ]],
        city: ['', Validators.maxLength(100)]
      })
    });

    this.setupFormListeners();
  }

  private setupFormListeners(): void {
    this.reservationForm.get('startDateTime')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateDateValidators();
      });

    this.reservationForm.get('reservationEventType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateAddressValidators();
      });
  }

  private populateForm(): void {
    if (!this.reservation) return;

    const startDateTimeLocal = this.apiDateToInputFormat(this.reservation.startDateTime);
    const endDateTimeLocal = this.apiDateToInputFormat(this.reservation.endDateTime);

    this.reservationForm.patchValue({
      eventName: this.reservation.eventName,
      reservationEventType: this.reservation.reservationEventType,
      requiredAgents: this.reservation.requiredAgents,
      startDateTime: startDateTimeLocal,
      endDateTime: endDateTimeLocal,
      description: this.reservation.description || '',
      address: {
        street: this.reservation.address?.street || '',
        streetNumber: this.reservation.address?.streetNumber || '',
        postalCode: this.reservation.address?.postalCode || '',
        city: this.reservation.address?.city || ''
      }
    });

    this.updateDateValidators();
    this.updateAddressValidators();
  }

  private apiDateToInputFormat(apiDateString: string): string {
    if (!apiDateString) return '';
    const [datePart, timePart] = apiDateString.split(' ');
    
    if (!datePart || !timePart) {
      console.error('Invalid date format:', apiDateString);
      return '';
    }
    
    const [day, month, year] = datePart.split('-');
    const [hours, minutes] = timePart.split(':');
    
    if (!day || !month || !year || !hours || !minutes) {
      console.error('Invalid date parts:', { day, month, year, hours, minutes });
      return '';
    }
    
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  }

  private updateDateValidators(): void {
    const startDate = this.reservationForm.get('startDateTime')?.value;
    if (startDate) {
      const startDateTime = new Date(startDate);
      this.minEndDate = new Date(startDateTime.getTime() + 3600000);
      
      const endDateControl = this.reservationForm.get('endDateTime');
      if (endDateControl?.value) {
        const endDateTime = new Date(endDateControl.value);
        if (endDateTime <= startDateTime) {
          endDateControl.setErrors({ ...endDateControl.errors, dateSequence: true });
        }
      }
    }
  }

  private updateAddressValidators(): void {
    const eventType = this.reservationForm.get('reservationEventType')?.value;
    const addressGroup = this.reservationForm.get('address') as FormGroup;
    
    const requiresAddress = [
      ReservationEventType.SECURITY_GUARDING,
      ReservationEventType.EVENT_SECURITY,
      ReservationEventType.PERSONAL_PROTECTION
    ].includes(eventType);

    Object.keys(addressGroup.controls).forEach(key => {
      const control = addressGroup.get(key);
      if (requiresAddress && key !== 'streetNumber') {
        control?.setValidators([Validators.required, ...(control.validator ? [control.validator] : [])]);
      } else {
        const currentValidators = control?.validator;
        if (currentValidators) {
          const newValidators = [];
          if (currentValidators.toString().includes('maxLength')) newValidators.push(Validators.maxLength(control?.errors?.['maxlength']?.requiredLength));
          if (currentValidators.toString().includes('pattern')) newValidators.push(Validators.pattern(control?.errors?.['pattern']?.requiredPattern));
          control?.setValidators(newValidators.length > 0 ? newValidators : null);
        } else {
          control?.clearValidators();
        }
      }
      control?.updateValueAndValidity();
    });
  }

  private futureDateValidator() {
    return (control: any) => {
      if (!control.value) return null;
      
      const selectedDate = new Date(control.value);
      const now = new Date();
      
      now.setHours(0, 0, 0, 0);
      
      return selectedDate >= now ? null : { pastDate: true };
    };
  }

  onSubmit(): void {
    if (this.reservationForm.invalid || this.isSubmitting) {
      this.markFormGroupTouched(this.reservationForm);
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.reservationForm.value;
    
    const updateData: ReservationUpdateDTO = {
      eventName: formValue.eventName,
      reservationEventType: formValue.reservationEventType,
      requiredAgents: formValue.requiredAgents,
      startDateTime: formValue.startDateTime,
      endDateTime: formValue.endDateTime,
      description: formValue.description || undefined,
      address: this.prepareAddress(formValue.address)
    };

    this.reservationService.updateReservation(this.reservationId, updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<ReservationDetail>) => {
          this.isSubmitting = false;
          this.successMessage = this.frenchTranslations.reservationUpdateSuccess;
          setTimeout(() => {
            this.notificationService.showSuccess('Réservation mise à jour avec succès');
            this.goToDetails();
          }, 500);
        },
        error: (error) => {
          this.isSubmitting = false;
          this.errorMessage = error.error?.message || this.frenchTranslations.reservationUpdateFailed;
          if (isPlatformBrowser(this.platformId)) {
            window.scrollTo({top: 0, behavior: 'smooth'});
          }
          console.error('Reservation update error:', error);
        }
      });
  }

  private prepareAddress(addressFormValue: any): Address {
    return {
      street: addressFormValue.street?.trim() || undefined,
      streetNumber: addressFormValue.streetNumber?.trim() || undefined,
      postalCode: addressFormValue.postalCode?.trim() || undefined,
      city: addressFormValue.city?.trim() || undefined
    };
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getEventTypeLabel(eventType: ReservationEventType): string {
    return this.frenchTranslations.eventTypes[eventType] || eventType;
  }

  get f() {
    if (!this.reservationForm || !this.reservationForm.controls) {
      return {};
    }
    return this.reservationForm.controls;
  }

  get addressForm(): FormGroup | null {
    const address = this.reservationForm?.get('address');
    return address instanceof FormGroup ? address : null;
  }

  hasError(controlName: string, errorName: string): boolean {
    const control = this.reservationForm?.get(controlName);
    return control ? control.hasError(errorName) && (control.touched || control.dirty) : false;
  }

  hasAddressError(controlName: string, errorName: string): boolean {
    const addressForm = this.addressForm;
    const control = addressForm?.get(controlName);
    return control ? control.hasError(errorName) && (control.touched || control.dirty) : false;
  }

  incrementAgents(): void {
    const currentValue = this.reservationForm.get('requiredAgents')?.value || 1;
    if (currentValue < 50) {
      this.reservationForm.get('requiredAgents')?.setValue(currentValue + 1);
    }
  }

  decrementAgents(): void {
    const currentValue = this.reservationForm.get('requiredAgents')?.value || 1;
    if (currentValue > 1) {
      this.reservationForm.get('requiredAgents')?.setValue(currentValue - 1);
    }
  }

  getErrorMessage(controlName: string, errorName: string): string {
    const errorMessages: { [key: string]: string } = {
      'eventName.required': this.frenchTranslations.eventNameRequired,
      'eventName.maxlength': this.frenchTranslations.eventNameMaxLength,
      'reservationEventType.required': this.frenchTranslations.eventTypeRequired,
      'requiredAgents.required': this.frenchTranslations.agentsRequired,
      'requiredAgents.min': this.frenchTranslations.agentsMin,
      'requiredAgents.max': this.frenchTranslations.agentsMax,
      'startDateTime.required': this.frenchTranslations.startDateRequired,
      'startDateTime.pastDate': this.frenchTranslations.startDateFuture,
      'endDateTime.required': this.frenchTranslations.endDateRequired,
      'endDateTime.pastDate': this.frenchTranslations.endDateFuture,
      'endDateTime.dateSequence': this.frenchTranslations.endDateAfterStart,
      'street.maxlength': this.frenchTranslations.streetMaxLength,
      'streetNumber.maxlength': this.frenchTranslations.streetNumberMaxLength,
      'postalCode.maxlength': this.frenchTranslations.postalCodeMaxLength,
      'postalCode.pattern': this.frenchTranslations.postalCodePattern,
      'city.maxlength': this.frenchTranslations.cityMaxLength,
      'description.maxlength': this.frenchTranslations.descriptionMaxLength,
      'street.required': this.frenchTranslations.streetRequired,
      'postalCode.required': this.frenchTranslations.postalCodeRequired,
      'city.required': this.frenchTranslations.cityRequired,
    };
    
    return errorMessages[`${controlName}.${errorName}`] || 'Erreur de validation';
  }

  goToDetails(): void {
    this.router.navigate(['/member/reservations/details', this.reservationId]);
  }

  goToList(): void {
    this.router.navigate(['/member/reservations']);
  }

  cancel(): void {
    this.goToDetails();
  }
}